import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DataError } from "../../core/errors/DataError";
import {
  LogoSharingInfo,
  OrganizationSharing,
  SharingChainShop,
} from "../../core/models/entities/LogoSharing";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import {
  mapToLogoSharingInfo,
  mapToOrganizationSharing,
  mapToSharingChainShop,
} from "../models/mappers/mapLogoSharing";
import {
  CreateSharingRelationBody,
  UpdateSharingRelationBody,
} from "../models/responses/LogoSharingDto";

// The sharing API (Etape 2) is built in parallel with this UI. Until it is
// deployed every endpoint answers 404 — the screens use this predicate to
// swap the error state for a friendly "sharing API not active yet" notice.
export function isSharingApiUnavailable(error: unknown): boolean {
  return (
    error instanceof DataError &&
    (error.errorStatus?.code === 404 || error.errorStatus?.code === 405)
  );
}

// No retries on 404/405 (the API simply isn't there yet — retrying only
// delays the empty state); one retry for anything else.
function sharingRetry(failureCount: number, error: unknown): boolean {
  if (isSharingApiUnavailable(error)) return false;
  return failureCount < 1;
}

// Resource + the query error, so screens can tell "sharing API not deployed
// yet" (404 → friendly notice) apart from a real failure (→ error + retry).
export type SharingResource<T> = Resource<T> & { error: unknown };

export interface LogoSharingRepository {
  // enabled=false keeps the query idle (used to defer fetches until the
  // collapsible sharing section is actually opened).
  useOrganizationSharing(
    orgId: number | undefined,
    options?: { enabled?: boolean },
  ): SharingResource<OrganizationSharing>;
  useSharingChain(
    orgId: number | undefined,
    options?: { enabled?: boolean },
  ): SharingResource<SharingChainShop[]>;
  useLogoSharing(
    logoId: number | undefined,
    options?: { enabled?: boolean },
  ): SharingResource<LogoSharingInfo>;
  createRelation(
    orgId: number,
    body: CreateSharingRelationBody,
  ): Promise<void>;
  updateRelation(
    relationId: number,
    body: UpdateSharingRelationBody,
  ): Promise<void>;
  getSelections(relationId: number): Promise<number[]>;
  saveSelections(relationId: number, logoIds: number[]): Promise<void>;
  setReceiverSuspension(
    orgId: number,
    receiverOrgId: number,
    suspended: boolean,
  ): Promise<void>;
  setGlobalExclusion(
    orgId: number,
    excluded: boolean,
    categories: string[],
  ): Promise<void>;
}

export function useLogoSharingRepository(): LogoSharingRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  async function invalidateSharing() {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["logo-sharing"],
        type: "all",
      }),
      queryClient.invalidateQueries({
        queryKey: ["logo-sharing-chain"],
        type: "all",
      }),
      queryClient.invalidateQueries({
        queryKey: ["logo-sharing-info"],
        type: "all",
      }),
    ]);
  }

  function useOrganizationSharing(
    orgId: number | undefined,
    options?: { enabled?: boolean },
  ): SharingResource<OrganizationSharing> {
    return useQuery(
      ["logo-sharing", orgId],
      () =>
        apiClient.getOrganizationSharing(orgId!).then(mapToOrganizationSharing),
      {
        enabled: !!orgId && (options?.enabled ?? true),
        retry: sharingRetry,
        staleTime: 30 * 1000,
      },
    );
  }

  function useSharingChain(
    orgId: number | undefined,
    options?: { enabled?: boolean },
  ): SharingResource<SharingChainShop[]> {
    return useQuery(
      ["logo-sharing-chain", orgId],
      () =>
        apiClient
          .getSharingChain(orgId!)
          .then((dtos) => dtos.map(mapToSharingChainShop)),
      {
        enabled: !!orgId && (options?.enabled ?? true),
        retry: sharingRetry,
        staleTime: 30 * 1000,
      },
    );
  }

  function useLogoSharing(
    logoId: number | undefined,
    options?: { enabled?: boolean },
  ): SharingResource<LogoSharingInfo> {
    return useQuery(
      ["logo-sharing-info", logoId],
      () => apiClient.getLogoSharing(logoId!).then(mapToLogoSharingInfo),
      {
        enabled: !!logoId && (options?.enabled ?? true),
        retry: sharingRetry,
        staleTime: 30 * 1000,
      },
    );
  }

  async function createRelation(
    orgId: number,
    body: CreateSharingRelationBody,
  ): Promise<void> {
    await apiClient.createSharingRelation(orgId, body);
    await invalidateSharing();
  }

  async function updateRelation(
    relationId: number,
    body: UpdateSharingRelationBody,
  ): Promise<void> {
    await apiClient.updateSharingRelation(relationId, body);
    await invalidateSharing();
  }

  async function getSelections(relationId: number): Promise<number[]> {
    const dto = await apiClient.getSharingRelationSelections(relationId);
    return dto.logoIds ?? [];
  }

  async function saveSelections(
    relationId: number,
    logoIds: number[],
  ): Promise<void> {
    await apiClient.updateSharingRelationSelections(relationId, logoIds);
    await invalidateSharing();
  }

  async function setReceiverSuspension(
    orgId: number,
    receiverOrgId: number,
    suspended: boolean,
  ): Promise<void> {
    await apiClient.updateSharingReceiverSuspension(
      orgId,
      receiverOrgId,
      suspended,
    );
    await invalidateSharing();
  }

  async function setGlobalExclusion(
    orgId: number,
    excluded: boolean,
    categories: string[],
  ): Promise<void> {
    await apiClient.updateSharingGlobalExclusion(orgId, excluded, categories);
    await invalidateSharing();
  }

  return {
    useOrganizationSharing,
    useSharingChain,
    useLogoSharing,
    createRelation,
    updateRelation,
    getSelections,
    saveSelections,
    setReceiverSuspension,
    setGlobalExclusion,
  };
}
